import uuidv4 from 'uuid/v4';
import _ from 'lodash';
import basic from './common/basic.js';
import rect from './common/rect.js';
import { unwrapMention, setMentionContent } from '../mention-helper.js';
import updater from '../updater.js';
import {
  roundToNearestMultiple,
  has,
  getTextContent,
  slugify,
} from '../../../utils.js';
import asset from './common/asset.js';
import mention from './common/mention.js';

/* State example:
state: {
  byId: {
    "elem_1": {
      "x": 10205,
      "y": 10469,
      "id": "elem_1",
      "theme": "cyan",
      "title": "Game start",
      "width": 138,
      "height": 50,
      "autoHeight": true,
      "content": "",
      "components": [],
      "linkedBoard": null
    },
    "elem_2": {
      "x": 10425,
      "y": 10293,
      "id": "elem_2",
      "theme": "purple",
      "title": "Introductory cutscene",
      "width": 410,
      "height": 387,
      "autoHeight": false,
      "content": "<p><i>Day, outside a village inn, west England 1850 A.D.</i></p><span class=\"mention mention-component\" contenteditable=\"false\" data-id=\"comp_2\" data-type=\"component\">Jim</span>&nbsp;is doing his daily chores at the <span class=\"mention mention-component\" contenteditable=\"false\" data-id=\"comp_10\" data-type=\"component\">Admiral Benbow Inn</span>&nbsp;when&nbsp;<span class=\"mention mention-component\" contenteditable=\"false\" data-id=\"comp_1\" data-type=\"component\">Black Dog</span>&nbsp;arrives and demands infomation about a patron,&nbsp;<span class=\"mention mention-component\" contenteditable=\"false\" data-id=\"comp_11\" data-type=\"component\">Billy Bones</span>.<p>When <span class=\"mention mention-component\" contenteditable=\"false\" data-id=\"comp_2\" data-type=\"component\">Jim</span>&nbsp;tries to escape, <span class=\"mention mention-component\" contenteditable=\"false\" data-id=\"comp_1\" data-type=\"component\">Black Dog</span>&nbsp;strikes him with the hilt of his cutlass and he loses consciousness.</p><p><i>Fade to Black.</i></p><p></p>",
      "components": [
        { id: "uuid1", component: "comp_2" },
        { id: "uuid2", component: "comp_1" }
      ],
      "linkedBoard": null
    },
    "elem_3": {
      "x": 11547,
      "y": 10100,
      "id": "elem_3",
      "theme": "red",
      "title": "<span class=\"mention mention-board\" contenteditable=\"false\" data-id=\"bo_2\" data-type=\"board\">Jim and Black Dog Dialog</span>&nbsp;",
      "width": 300,
      "height": 223.39999956000005,
      "autoHeight": true,
      "content": "<span class=\"mention mention-component\" contenteditable=\"false\" data-id=\"comp_2\" data-type=\"component\">Jim</span>&nbsp;talks to <span class=\"mention mention-component\" contenteditable=\"false\" data-id=\"comp_1\" data-type=\"component\">Black Dog</span>&nbsp;to find out more information about him and perhaps distract him.",
      "components": [
        { id: "uuid1", component: "comp_2" },
        { id: "uuid2", component: "comp_1" }
      ],
      "linkedBoard": null
    },
    "elem_4": {
      "x": 12040,
      "y": 10614,
      "id": "elem_4",
      "theme": "purple",
      "title": "Cutting the rope",
      "width": 289,
      "height": 248,
      "autoHeight": true,
      "content": "<span class=\"mention mention-component\" contenteditable=\"false\" data-id=\"comp_2\" data-type=\"component\">Jim</span>&nbsp;starts cutting the rope with the&nbsp;<span class=\"mention mention-component\" contenteditable=\"false\" data-id=\"comp_8\" data-type=\"component\">Piece of broken glass</span>. He has about 10 seconds before <span class=\"mention mention-component\" contenteditable=\"false\" data-id=\"comp_1\" data-type=\"component\">Black Dog</span>&nbsp;returns to his position.",
      "components": [
        "comp_2"
      ],
      "linkedBoard": null
    },
  }
}
*/
const NEW_ELEMENT = {
  title: '',
  content: '',
  width: 301,
  height: 121,
  autoHeight: true,
  theme: 'default',
  components: [],
  outputs: [],
};
const getterOptions = {
  container: { type: 'boards', prop: 'elements' },
  mentionType: 'element',
};

const actionOptions = {
  type: 'elements',
  container: { type: 'boards', prop: 'elements' },
};

const state = { byId: {}, startingElement: null };

const getters = {
  ...basic.getters(getterOptions),
  ...rect.getters(getterOptions),
  ...asset.getters(),
  ...mention.getters(getterOptions),

  /**
   * Get the components of this element
   * @param   {string}  id  The element id
   * @returns {array}       The components in an array of the form { id, component }
   */
  getComponents(state) {
    return id => {
      return state.byId[id]?.components;
    };
  },

  /**
   * Get the outputs of this element
   * @param   {string} id     The element id
   * @returns {array}         The outputs of the element
   */
  getOutputs(state) {
    return id => {
      return state.byId[id]?.outputs;
    };
  },

  /**
   * Return the starting element Id
   * @param {Object} state
   */
  getStartingElement(state) {
    return state.startingElement;
  },

  getMention(state, getters, rootState, rootGetters) {
    return id => {
      const node = getters.getNode(id);
      if (!node) {
        return null;
      }
      const coverId = getters.getCoverId(node.id);
      const name = getTextContent(node.title);
      return {
        mention_image:
          rootGetters['project/data/assets/getSmallImageUrl'](coverId),
        mention_icon: getters.getCoverIcon(coverId) || getters.getDefaultIcon,
        mention_name: name?.length > 0 ? name : 'Untitled Element',
        mention_type: getterOptions.mentionType,
        mention_id: node.id,
        mention_theme: node.theme,
      };
    };
  },

  /**
   * Return the element's attributes
   * @param   {string} id   The element id
   * @returns {array}       The attribute IDs
   */
  getAttributes(state, getters) {
    return id => {
      const node = getters.getNode(id);
      if (!node) {
        return [];
      }
      if (Array.isArray(node.attributes)) {
        return node.attributes;
      }
      return [];
    };
  },
};

const actions = {
  ...basic.actions(actionOptions),
  ...asset.actions(actionOptions),

  /**
   * Create a new element and any customization to default keys, values are passed through customProperties
   * @param {Number}  boardId   The board into which that will be created
   * @param {Object}  position  The position onto the board
   * @param {Object}  props     The custom properties overriding the default
   * @param {Boolean} apply     Whether to apply the patch after finishing
   */
  customCreate(context, { boardId, position, props = {}, apply }) {
    // Create the element
    const id = uuidv4();
    let x = position.x - NEW_ELEMENT.width / 2;
    let y = position.y - NEW_ELEMENT.height / 2;

    // Constrain the element position to never leave the board limits
    x = Math.max(x, 0);
    y = Math.max(y, 0);
    x = Math.min(x, 20000 - NEW_ELEMENT.width);
    y = Math.min(y, 20000 - NEW_ELEMENT.height);

    const { snap, size } = context.rootGetters['project/config/getGrid'];
    if (snap) {
      x = roundToNearestMultiple(x, size);
      y = roundToNearestMultiple(y, size);
    }
    // Apply also custom default styles
    const defaultStyle =
      context.rootGetters['project/config/elements/getDefaultStyle'];

    const element = { id, ...NEW_ELEMENT, ...defaultStyle, x, y, ...props };
    context.dispatch('create', {
      node: element,
      tId: boardId,
      tIndex: '-',
      apply: false,
    });
    updater.complete({ apply });
    return Promise.resolve(id);
  },

  /**
   * Update some of the element's properties (element id should be filled)
   * @param {String}  id      The element id
   * @param {Object}  props   The properties that are new or changed
   * @param {Boolean} apply   Whether to apply the patch after finishing
   */
  customUpdate(context, { id, props, apply }) {
    context.dispatch('update', { id, props, apply: false });
    if (has(props, 'title')) {
      const title = getTextContent(props.title);
      ['elements', 'connections', 'conditions'].forEach(name => {
        context.dispatch(
          `project/data/${name}/updateMentions`,
          {
            id,
            type: 'element',
            label: title,
            name: slugify(title),
            apply: false,
          },
          { root: true }
        );
      });

      context.dispatch(
        `application/clipboard/updateMentions`,
        {
          id,
          type: 'element',
          label: title,
          name: slugify(title),
        },
        { root: true }
      );
    }
    updater.complete({ apply });
  },

  /**
   * Remove an element with specified id from the store
   * @param {String}  id      The element id
   * @param {Boolean} apply   Whether to apply the patch after finishing
   */
  customDelete(context, { id, apply }) {
    // Check if it is starting element and if it is setStartingElement to null
    if (id === context.getters.getStartingElement) {
      context.dispatch('setStartingElement', { id: null, apply: false });
    }
    // Remove any mentions in elements, conditions, connections
    ['elements', 'conditions', 'connections'].forEach(name => {
      context.dispatch(
        `project/data/${name}/deleteMentions`,
        { ids: [id], type: 'element', apply: false },
        { root: true }
      );
    });

    // Remove any mentions in clipboard
    context.dispatch(
      'application/clipboard/deleteMentions',
      { ids: [id], type: 'element' },
      { root: true }
    );
    context.dispatch(
      'application/clipboard/deleteElementsFromJumpers',
      { elementIds: [id] },
      { root: true }
    );

    // Delete its connections
    const connectionIds =
      context.rootGetters['project/data/connections/getConnections'](id);
    context.dispatch(
      'project/data/connections/deleteReferences',
      { ids: connectionIds, apply: false },
      { root: true }
    );
    context.dispatch(
      'project/data/connections/deleteMany',
      { ids: connectionIds, apply: false },
      { root: true }
    );

    // Delete its attributes
    const attributeIds =
      context.rootGetters['project/data/elements/getAttributes'](id);
    context.dispatch(
      'project/data/attributes/deleteMany',
      { ids: attributeIds, deleteReference: false, apply: false },
      { root: true }
    );

    // Clear its jumpers
    context.dispatch(
      'project/data/jumpers/clearElementJumpers',
      { id, apply: false },
      { root: true }
    );
    // Deselect it
    context.dispatch(
      'project/config/elements/deselect',
      { id, apply: false },
      { root: true }
    );
    // Delete the element
    context.dispatch('delete', { id, apply: false });

    updater.complete({ apply });
  },

  /**
   * Add a component reference to an element 'components' list
   * @param {String}  id          The element id
   * @param {String}  componentId The component id
   * @param {Number}  index       The index in the list
   * @param {Boolean} apply       Whether to apply the patch
   */
  addComponent(context, { id, componentId, index, apply }) {
    const state = _.cloneDeep(context.getters.getComponents(id));
    const patch = [
      {
        op: 'add',
        path: `/data/elements/byId/${id}/components/${index}`,
        value: { id: uuidv4(), component: componentId },
        state,
      },
    ];
    updater.add(patch);
    updater.complete({ apply });
  },

  /**
   * Remove a component reference from an element's 'components' list
   * @param {String}  id      The element id
   * @param {String}  index   The index to remove
   * @param {Boolean} apply   Whether to apply the patch after finishing
   */
  deleteComponent(context, { id, index, apply }) {
    const state = _.cloneDeep(context.getters.getComponents(id));
    const patch = [
      {
        op: 'remove',
        path: `/data/elements/byId/${id}/components/${index}`,
        state,
      },
    ];
    updater.add(patch);
    updater.complete({ apply });
  },

  /**
   * Add a cnnection reference to an element 'outputs' list
   * @param {String}  id          The element id
   * @param {String}  outputId    The output id
   * @param {Number}  index       The index in the list
   * @param {Boolean} apply       Whether to apply the patch
   */
  addOutput(context, { id, outputId, index, apply }) {
    // const state = _.cloneDeep(context.getters.getOutputs(id));
    const patch = [
      {
        op: 'add',
        path: `/data/elements/byId/${id}/outputs/${index}`,
        value: outputId,
        // state,
      },
    ];
    updater.add(patch);
    updater.complete({ apply });
  },

  /**
   * Remove a connection output from the element's output list
   * @param {String}  id        The element id
   * @param {String}  outputId  The output to remove
   * @param {Boolean} apply     Whether to apply the patch after finishing
   */
  deleteOutput(context, { id, outputId, apply }) {
    const state = _.cloneDeep(context.getters.getOutputs(id));
    const index = state.findIndex(conId => conId === outputId);
    const patch = [
      {
        op: 'remove',
        path: `/data/elements/byId/${id}/outputs/${index}`,
        state,
      },
    ];
    updater.add(patch);
    updater.complete({ apply });
  },

  /**
   * Move a component reference from an element 'components' list to another index (or another element)
   * @param {String}  sId     The element id (the old host)
   * @param {String}  sIndex  The old index
   * @param {String}  tId     The element id (the new host)
   * @param {String}  tIndex  The new index
   * @param {Boolean} apply   Whether to apply the patch after finishing
   */
  moveComponent(context, { sId, sIndex, tId, tIndex, apply }) {
    const fromState = _.cloneDeep(context.getters.getComponents(sId));
    const state = _.cloneDeep(context.getters.getComponents(tId));
    const component = fromState[sIndex];
    const index = state.findIndex(el => _.isEqual(el, component));
    if (index !== -1 && index < tIndex) {
      tIndex -= 1;
    }
    const patch = [
      {
        op: 'move',
        from: `/data/elements/byId/${sId}/components/${sIndex}`,
        path: `/data/elements/byId/${tId}/components/${tIndex}`,
        fromState,
        state,
      },
    ];
    updater.add(patch);
    updater.complete({ apply });
  },

  /**
   * Move an output to another position in the same element
   * @param {String}  sId     The element id
   * @param {String}  sIndex  The old index
   * @param {String}  tIndex  The new index
   * @param {Boolean} apply   Whether to apply the patch after finishing
   */
  moveOutput(context, { sId, sIndex, tIndex, apply }) {
    const fromState = _.cloneDeep(context.getters.getOutputs(sId));
    const state = _.cloneDeep(context.getters.getOutputs(sId));
    const output = fromState[sIndex];
    const index = state.findIndex(el => _.isEqual(el, output));
    if (index !== -1 && index < tIndex) {
      tIndex -= 1;
    }
    const patch = [
      {
        op: 'move',
        from: `/data/elements/byId/${sId}/outputs/${sIndex}`,
        path: `/data/elements/byId/${sId}/outputs/${tIndex}`,
        fromState,
        state,
      },
    ];
    updater.add(patch);
    updater.complete({ apply });
  },

  /**
   * Set the board reference to an element's 'linkedBoard'
   * @param {String}  id      The element id
   * @param {String}  boardId The board id
   * @param {Boolean} apply   Whether to apply the patch after finishing
   */
  setBoard(context, { id, boardId, apply }) {
    const patch = [
      {
        op: 'add',
        path: `/data/elements/byId/${id}/linkedBoard`,
        value: boardId,
      },
    ];
    updater.add(patch);
    updater.complete({ apply });
  },

  /**
   * Deletes 'linkedBoard' reference from an element
   * @param {String}  id      The element id
   * @param {String}  boardId The board id
   * @param {Boolean} apply   Whether to apply the patch after finishing
   */
  deleteLinkedBoard(context, { id, boardId, apply }) {
    const patch = [
      {
        op: 'remove',
        path: `/data/elements/byId/${id}/linkedBoard`,
        value: boardId,
      },
    ];
    updater.add(patch);
    updater.complete({ apply });
  },

  /**
   * Remove the specified component references from all elements 'components' list
   * @param {array}   ids      The component ids to remove
   * @param {Boolean} apply   Whether to apply the patch after finishing
   */
  deleteComponents(context, { ids, apply }) {
    const patch = [];
    // Remove the components from any component list
    Object.values(context.state.byId)
      .filter(
        element =>
          has(element, 'components') &&
          element.components.some(c => ids.includes(c.component))
      )
      .forEach(element => {
        let index = element.components.length;
        // Remove the components looping the component list from end to start
        while (index > 0) {
          index -= 1;

          if (ids.includes(element.components[index].component)) {
            // Adjust the index (if there are operations on this array already on the patch)
            const adjIndex = updater.getIndexAdjustment(
              `/data/elements/byId/${element.id}/components/${index}`
            );

            patch.push({
              op: 'remove',
              path: `/data/elements/byId/${element.id}/components/${adjIndex}`,
            });
          }
        }
      });
    updater.add(patch);
    updater.complete({ apply });
  },

  /**
   * Remove boards from all elements 'linkedBoard'
   * @param {array}   ids      The board ids
   * @param {Boolean} apply    Whether to apply the patch after finishing
   */
  deleteBoards(context, { ids, apply }) {
    const patch = [];
    // Remove the board from the list
    Object.values(context.state.byId)
      .filter(
        element =>
          has(element, 'linkedBoard') && ids.includes(element.linkedBoard)
      )
      .forEach(element => {
        patch.push({
          op: 'remove',
          path: `/data/elements/byId/${element.id}/linkedBoard`,
          value: null,
        });
      });
    updater.add(patch);
    updater.complete({ apply });
  },

  /**
   * Remove output from all elements outputs
   * @param {array}   ids   The output ids
   * @param {Boolean} apply Whether to apply the patch after finishing
   */
  deleteOutputs(context, { ids, apply }) {
    const patch = [];
    // Remove the connections from any output list
    Object.values(context.state.byId)
      .filter(
        element =>
          has(element, 'outputs') &&
          element.outputs.some(id => ids.includes(id))
      )
      .forEach(element => {
        let index = element.outputs.length;
        // Remove the outputs looping the output list from end to start
        while (index > 0) {
          index -= 1;

          if (ids.includes(element.outputs[index])) {
            // Adjust the index (if there are operations on this array already on the patch)
            const adjIndex = updater.getIndexAdjustment(
              `/data/elements/byId/${element.id}/outputs/${index}`
            );

            patch.push({
              op: 'remove',
              path: `/data/elements/byId/${element.id}/outputs/${adjIndex}`,
            });
          }
        }
      });
    updater.add(patch);
    updater.complete({ apply });
  },

  /**
   * Remove mentions from all elements.
   * @param {array}   ids       The ids of the mentioned objects
   * @param {String}  type      The type of the mentioned objects
   */
  deleteMentions(context, { ids, type, apply }) {
    const patch = [];
    Object.values(context.state.byId).forEach(element => {
      let text = unwrapMention(element.content, ids, type);
      if (text !== element.content) {
        patch.push({
          op: 'replace',
          path: `/data/elements/byId/${element.id}/content`,
          value: text,
        });
      }
      text = unwrapMention(element.title, ids, type);
      if (text !== element.title) {
        patch.push({
          op: 'replace',
          path: `/data/elements/byId/${element.id}/title`,
          value: text,
        });
      }
    });
    updater.add(patch);
    updater.complete({ apply });
  },

  /**
   * Update the name in a mention in all elements.
   * @param {String} id       The id of the mentioned object
   * @param {String} type     The type of the mentioned object
   * @param {String} name     The new name of the mentioned object
   * @param {String}  label The original label of the mention if different from name
   */
  updateMentions(context, { id, type, name, label, apply }) {
    const patch = [];
    Object.values(context.state.byId).forEach(element => {
      let text = setMentionContent(element.content, id, type, name, label);
      if (text !== element.content) {
        patch.push({
          op: 'replace',
          path: `/data/elements/byId/${element.id}/content`,
          value: text,
        });
      }
      if (!(getterOptions.mentionType === type && element.id === id)) {
        text = setMentionContent(element.title, id, type, name, label);
        if (text !== element.title) {
          patch.push({
            op: 'replace',
            path: `/data/elements/byId/${element.id}/title`,
            value: text,
          });
        }
      }
    });
    updater.add(patch);
    updater.complete({ apply });
  },

  /**
   * Update the starting element.
   * @param {String} id       The id of the element
   */
  setStartingElement(context, { id, apply }) {
    const patch = [];
    patch.push({
      op: 'replace',
      path: '/data/elements/startingElement',
      value: id,
    });

    updater.add(patch);
    updater.complete({ apply });
  },
};

const mutations = {};

const module = {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};

export default module;
